// =====================================================================
// Faz AQ (2026-05-07) — İade Talepleri Moderasyon
// =====================================================================
// Owner müşteri iade taleplerini onaylar/reddeder. Onaylanan talepler
// için para iadesi (manuel BH iptal + ödeme iadesi) yapıldıktan sonra
// "refunded" işaretler.
//
// Endpoint:
//   GET  /api/admin/returns?status=...
//   PUT  /api/admin/returns/:id/approve (refund_amount + admin_note)
//   PUT  /api/admin/returns/:id/reject (admin_note zorunlu)
//   PUT  /api/admin/returns/:id/refund (approved → refunded)
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, CircularProgress, Alert, IconButton,
  Tooltip, Button, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PaymentsIcon from "@mui/icons-material/Payments";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " ₺";
const fmtDate = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "—";

// Status → renkli chip
const STATUS_META = {
  pending:  { color: "#f59e0b", label: "Bekliyor" },
  approved: { color: "#10b981", label: "Onaylandı" },
  rejected: { color: "#ef4444", label: "Reddedildi" },
  refunded: { color: "#3b82f6", label: "Para İade Edildi" }
};

// Reason enum → Türkçe etiket + ikon/renk
const REASON_META = {
  damaged:        { label: "Hasarlı geldi",       color: "#ef4444", icon: "💥" },
  wrong_product:  { label: "Yanlış ürün",         color: "#f59e0b", icon: "❌" },
  quality_issue:  { label: "Kalite sorunu",       color: "#dc2626", icon: "⚠️" },
  changed_mind:   { label: "Vazgeçti",            color: "#94a3b8", icon: "🤷" },
  other:          { label: "Diğer",               color: "#64748b", icon: "❓" }
};

export default function Returns() {
  const [returns, setReturns] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Approve dialog state
  const [approveDialog, setApproveDialog] = useState({ open: false, item: null });
  const [refundAmount, setRefundAmount] = useState(0);
  const [adminNote, setAdminNote] = useState("");

  const load = () => {
    setLoading(true);
    API.get(`/api/admin/returns?status=${filter}`)
      .then(r => {
        setReturns(r.data.items || []);
        setCounts(r.data.counts || {});
        setErr(null);
      })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const openApprove = (item) => {
    setApproveDialog({ open: true, item });
    setRefundAmount(Number(item.order_total) || 0);  // Default: full refund
    setAdminNote("");
  };

  const submitApprove = async () => {
    try {
      await API.put(`/api/admin/returns/${approveDialog.item.id}/approve`, {
        refund_amount: refundAmount,
        admin_note: adminNote
      });
      toast.success("Onaylandı");
      setApproveDialog({ open: false, item: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const reject = async (item) => {
    const note = window.prompt("Red sebebi (zorunlu — müşteriye gösterilir):");
    if (!note || note.trim().length === 0) {
      toast.error("Red sebebi zorunlu");
      return;
    }
    try {
      await API.put(`/api/admin/returns/${item.id}/reject`, { admin_note: note });
      toast.success("Reddedildi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const markRefunded = async (item) => {
    if (!window.confirm(`${fmtMoney(item.refund_amount)} para iadesi yapıldı mı? Onaylarsan refunded olarak işaretlenecek.`)) return;
    try {
      await API.put(`/api/admin/returns/${item.id}/refund`);
      toast.success("Para iadesi tamamlandı olarak işaretlendi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  if (loading && returns.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>İade Talepleri</Typography>
          <Typography variant="body2" color="text.secondary">
            Müşteri iade talepleri moderasyonu — yasal/operasyonel öncelik
          </Typography>
        </Box>
        <Tooltip title="Yenile"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* KPI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#fffbeb" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>BEKLEYEN</Typography>
            <Typography variant="h4" fontWeight={800} color="#f59e0b">{Number(counts.pending) || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#f0fdf4" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>ONAYLANDI</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{Number(counts.approved) || 0}</Typography>
            <Typography variant="caption" color="text.disabled">para iadesi bekliyor</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#eff6ff" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>REFUNDED</Typography>
            <Typography variant="h4" fontWeight={800} color="#3b82f6">{Number(counts.refunded) || 0}</Typography>
            <Typography variant="caption" color="text.disabled">para iadesi yapıldı</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>TOPLAM İADE TUTARI</Typography>
            <Typography variant="h4" fontWeight={800}>{fmtMoney(counts.total_refunded)}</Typography>
            <Typography variant="caption" color="text.disabled">approved + refunded</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filter */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" value={filter} exclusive onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="pending">Bekleyen ({counts.pending || 0})</ToggleButton>
          <ToggleButton value="approved">Onaylı ({counts.approved || 0})</ToggleButton>
          <ToggleButton value="refunded">İade Edildi</ToggleButton>
          <ToggleButton value="rejected">Red</ToggleButton>
          <ToggleButton value="all">Tümü</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Cards */}
      {returns.length === 0 ? (
        <Alert severity={filter === "pending" ? "success" : "info"}>
          {filter === "pending" ? "✅ Onay bekleyen iade talebi yok" : "Bu kriterde talep yok"}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {returns.map(r => {
            const sm = STATUS_META[r.status] || STATUS_META.pending;
            const rm = REASON_META[r.reason] || REASON_META.other;
            const hours = Math.round((Date.now() - new Date(r.created_at)) / 3600000);
            return (
              <Grid item xs={12} md={6} key={r.id}>
                <Card sx={{
                  p: 2, borderRadius: 3, height: "100%",
                  borderLeft: `4px solid ${sm.color}`
                }}>
                  {/* Üst: sebep + status */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                    <Typography fontSize={20}>{rm.icon}</Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontSize={14} fontWeight={700}>İade #{r.id} — {rm.label}</Typography>
                      <Typography variant="caption" color="text.disabled" fontSize={11}>
                        Sipariş #{r.order_id} · {fmtMoney(r.order_total)} · {hours}sa önce
                      </Typography>
                    </Box>
                    <Chip label={sm.label} size="small" sx={{ bgcolor: sm.color, color: "white", fontWeight: 700 }} />
                  </Box>

                  {/* Açıklama */}
                  {r.reason_detail && (
                    <Box sx={{ p: 1.2, bgcolor: "#f8fafc", borderRadius: 1, mb: 1.5 }}>
                      <Typography variant="caption" color="text.disabled" fontSize={10} fontWeight={700}>MÜŞTERİ AÇIKLAMA:</Typography>
                      <Typography fontSize={13} sx={{ whiteSpace: "pre-wrap", mt: 0.3 }}>
                        {r.reason_detail}
                      </Typography>
                    </Box>
                  )}

                  {/* Müşteri info */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography fontSize={12} fontWeight={600}>{r.company_name || r.user_name}</Typography>
                    <Typography variant="caption" color="text.disabled" fontSize={11}>
                      {r.email} {r.phone ? `· ${r.phone}` : ""}
                    </Typography>
                  </Box>

                  {/* Refund + admin note (varsa) */}
                  {r.refund_amount > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", p: 1, bgcolor: "#f0f9ff", borderRadius: 1, mb: 1 }}>
                      <Typography fontSize={12} color="text.secondary">İade Tutarı:</Typography>
                      <Typography fontSize={14} fontWeight={800} color="#1565C0">{fmtMoney(r.refund_amount)}</Typography>
                    </Box>
                  )}
                  {r.admin_note && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: "block", fontStyle: "italic", mb: 1, fontSize: 11 }}>
                      💬 {r.admin_note}
                    </Typography>
                  )}

                  {/* Aksiyon butonları */}
                  {r.status === "pending" && (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="contained" size="small" startIcon={<CheckIcon />}
                        onClick={() => openApprove(r)}
                        sx={{ bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" }, flex: 1 }}
                      >
                        Onayla
                      </Button>
                      <Button
                        variant="outlined" size="small" startIcon={<CloseIcon />}
                        onClick={() => reject(r)} color="error"
                        sx={{ flex: 1 }}
                      >
                        Reddet
                      </Button>
                    </Box>
                  )}
                  {r.status === "approved" && (
                    <Button
                      variant="contained" size="small" startIcon={<PaymentsIcon />}
                      onClick={() => markRefunded(r)} fullWidth
                      sx={{ bgcolor: "#3b82f6" }}
                    >
                      Para İadesi Yapıldı (refunded işaretle)
                    </Button>
                  )}
                  {(r.status === "refunded" || r.status === "rejected") && r.moderated_at && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: "block", fontSize: 11 }}>
                      Karar: {fmtDate(r.moderated_at)}
                    </Typography>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Approve Dialog — refund_amount + note */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle>İade Talebini Onayla — #{approveDialog.item?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>Sipariş #{approveDialog.item?.order_id}</strong> ·
            Toplam: {fmtMoney(approveDialog.item?.order_total)}
          </Typography>
          <TextField
            fullWidth type="number" label="İade Tutarı (₺)"
            value={refundAmount}
            onChange={e => setRefundAmount(parseFloat(e.target.value) || 0)}
            helperText="Tam iade veya kısmi iade tutarı"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth multiline rows={3} label="Admin Notu (opsiyonel)"
            value={adminNote}
            onChange={e => setAdminNote(e.target.value)}
            placeholder="İade kabul edildi, kargo ile geri gönderecek..."
          />
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1.5 }}>
            ⚠️ Onayladıktan sonra para iadesi <strong>manuel</strong> yapılır (havale/iade).
            Para gönderildikten sonra "Para İadesi Yapıldı" butonuyla refunded olarak işaretle.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, item: null })}>İptal</Button>
          <Button variant="contained" onClick={submitApprove} sx={{ bgcolor: "#10b981" }}>
            Onayla
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
